// src/store/slices/notificationSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Notification {
  id?: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  autoHideDuration?: number
  dismissed?: boolean
  timestamp?: number
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
}

// Helper to generate unique ID
const generateId = () => `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Add a new notification
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'dismissed' | 'timestamp'>>) => {
      const newNotification: Notification = {
        id: generateId(),
        dismissed: false,
        timestamp: Date.now(),
        ...action.payload,
      }
      state.notifications.unshift(newNotification)
      state.unreadCount += 1
    },

    // Remove a notification by ID
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex(notification => notification.id === action.payload)
      if (index !== -1) {
        if (!state.notifications[index].dismissed) {
          state.unreadCount -= 1
        }
        state.notifications.splice(index, 1)
      }
    },

    // Mark a notification as dismissed
    dismissNotification: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload)
      if (notification && !notification.dismissed) {
        notification.dismissed = true
        state.unreadCount -= 1
      }
    },

    // Mark all notifications as read
    markAllAsRead: (state) => {
      state.notifications.forEach(notification => {
        if (!notification.dismissed) {
          notification.dismissed = true
        }
      })
      state.unreadCount = 0
    },

    // Clear all notifications
    clearAllNotifications: (state) => {
      state.notifications = []
      state.unreadCount = 0
    },

    // Remove all dismissed notifications
    clearDismissedNotifications: (state) => {
      state.notifications = state.notifications.filter(notification => !notification.dismissed)
    },

    // Update notification (useful for extending autoHideDuration, etc.)
    updateNotification: (state, action: PayloadAction<{ id: string; updates: Partial<Notification> }>) => {
      const notification = state.notifications.find(n => n.id === action.payload.id)
      if (notification) {
        Object.assign(notification, action.payload.updates)
      }
    },
  },
})

export const {
  addNotification,
  removeNotification,
  dismissNotification,
  markAllAsRead,
  clearAllNotifications,
  clearDismissedNotifications,
  updateNotification,
} = notificationSlice.actions

// Selectors
export const selectAllNotifications = (state: { notifications: NotificationState }) => 
  state.notifications.notifications

export const selectUnreadNotifications = (state: { notifications: NotificationState }) => 
  state.notifications.notifications.filter(notification => !notification.dismissed)

export const selectUnreadCount = (state: { notifications: NotificationState }) => 
  state.notifications.unreadCount

export const selectNotificationsByType = (type: Notification['type']) => 
  (state: { notifications: NotificationState }) => 
    state.notifications.notifications.filter(notification => notification.type === type)

export default notificationSlice.reducer